'use client';

import type { SuggestedFeaturesProps } from '@/core/types';

import React from 'react';

import { Box, Image, Container, Typography } from '@/components/ui';

export default function SuggestedFeatures({
  title,
  description,
  items,
  image,
  className = '',
}: SuggestedFeaturesProps) {
  return (
    <section className={`relative ${className}`}>
      {Object.keys(image || {}).length > 0 && (
        <Image
          src={image?.url || ''}
          alt={image?.alt || ''}
          objectFit="cover"
          className="w-full absolute top-0 left-0 h-full object-cover -z-[1]"
          loading="lazy"
          width={1000}
          height={300}
        />
      )}

      <Container>
        <Box className="text-center mb-8 lg:mb-16 max-w-md">
          {title && (
            <Typography variant="h2" className="uppercase text-3xl md:text-4xl font-bold mb-3">
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              variant="body1"
              paragraph
              className="text-base md:text-2xl max-w-3xl mx-auto"
            >
              {description}
            </Typography>
          )}
        </Box>

        <Box className="flex flex-wrap justify-center  xl:grid  xl:grid-cols-[repeat(5,_minmax(150px,_1fr))] gap-12 gap-y-4 lg:gap-y-12">
          {items?.map((item) => (
            <Box key={item.id} className="flex flex-col sm:w-[44%] lg:w-[28%] xl:w-auto">
              <Typography
                variant="h5"
                className="relative text-base uppercase font-semibold mb-3 text-white pt-[48px]"
              >
                <Box className="absolute top-0 -left-[58px]">
                  <StepMarker />
                </Box>
                <span className="text-primary block">{item.title}</span>
                {item.subtitle}
              </Typography>
              <Typography variant="body2" className="text-sm text-white">
                {item.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </section>
  );
}

function StepMarker() {
  return (
    <svg width="80" height="80" viewBox="0 0 93 93" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask
        id="mask0_2463_1677"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="93"
        height="93"
      >
        <rect
          x="92.3354"
          y="28.3066"
          width="90.8418"
          height="38.6582"
          transform="rotate(135 92.3354 28.3066)"
          fill="#D9D9D9"
        />
      </mask>
      <g mask="url(#mask0_2463_1677)">
        <path
          d="M93.0795 84.6719C77.7423 100.009 52.7956 100.009 37.4585 84.6718C22.1213 69.3347 22.1213 44.388 37.4585 29.0508C52.7956 13.7137 77.7423 13.7137 93.0795 29.0508C108.417 44.388 108.417 69.3347 93.0795 84.6719ZM38.1656 29.7579C23.2173 44.7062 23.2244 69.0236 38.1656 83.9647C53.1067 98.9059 77.4312 98.9059 92.3724 83.9648C107.314 69.0236 107.314 44.6991 92.3724 29.7579C77.4312 14.8168 53.1067 14.8168 38.1656 29.7579Z"
          fill="#6D6E71"
        />
        <path
          d="M82.3033 73.8955C72.9058 83.293 57.6253 83.2859 48.2349 73.8955C38.8445 64.5052 38.8445 49.2175 48.2349 39.8271C57.6253 30.4368 72.9129 30.4367 82.3033 39.8271C91.6937 49.2175 91.6937 64.5052 82.3033 73.8955ZM50.3491 41.9414C42.1255 50.165 42.1255 63.5435 50.3491 71.7671C58.5728 79.9908 71.9512 79.9908 80.1749 71.7671C88.3985 63.5435 88.3986 50.165 80.1749 41.9414C71.9513 33.7177 58.5728 33.7177 50.3491 41.9414Z"
          fill="#3BD182"
        />
        <path
          d="M73.259 64.8517C77.6719 60.4387 77.6719 53.284 73.259 48.871C68.8461 44.4581 61.6913 44.4581 57.2784 48.871C52.8655 53.284 52.8655 60.4387 57.2784 64.8516C61.6913 69.2646 68.8461 69.2646 73.259 64.8517Z"
          fill="#3BD182"
        />
        <path
          d="M101.873 93.7078C81.8544 113.726 49.285 113.726 29.2739 93.7148L29.981 93.0077C49.6032 112.63 81.5362 112.63 101.165 93.0007L101.873 93.7078Z"
          fill="#6D6E71"
        />
      </g>
    </svg>
  );
}
